import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Skeleton,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic'
import { useTranslation } from 'react-i18next'

import { useDashboard, useConfirmIncident } from '../hooks/useDashboard'
import { useActiveIncident } from '../hooks/useActiveIncident'
import { useAccount } from '../hooks/useAccount'
import type { ShiftDto, IncidentResponse } from '../types'
import dayjs from 'dayjs'

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: active ? '#4caf50' : '#f44336',
        flexShrink: 0,
      }}
    />
  )
}

// ─── Status chip ──────────────────────────────────────────────────────────────

function IncidentStatusChip({ status }: { status: string }) {
  const { t } = useTranslation()
  type ChipColor = 'error' | 'success' | 'default' | 'warning' | 'info'
  const colorMap: Record<string, ChipColor> = {
    ACTIVE:    'error',
    CONFIRMED: 'success',
    STALLED:   'warning',
    TIMEOUT:   'info',
    SILENT:    'default',
  }
  return (
    <Chip
      label={t(`incidentStatus.${status}`, { defaultValue: status })}
      color={colorMap[status] ?? 'default'}
      size="small"
      sx={{ color: '#fff' }}
    />
  )
}

// ─── Active incident card ─────────────────────────────────────────────────────

interface ActiveIncidentCardProps {
  incident: IncidentResponse
  tz: string
}

function ActiveIncidentCard({ incident, tz }: ActiveIncidentCardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mutate: confirm, isPending } = useConfirmIncident()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Card
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'error.main',
        background: isDark ? 'rgba(211,47,47,0.08)' : 'rgba(211,47,47,0.05)',
      }}
    >
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <WarningAmberIcon sx={{ color: 'error.main', fontSize: 28, flexShrink: 0 }} />
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={700} color="error.main">
              {t('dashboard.activeIncidentTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.activeIncidentStarted', { time: dayjs(incident.startedAt).tz(tz).format('MMM D, HH:mm') })}
              {' · '}
              {dayjs(incident.startedAt).fromNow()}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => confirm(incident.incidentId)}
            disabled={isPending}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', color: '#fff' }}
          >
            {t('dashboard.confirmIncident')}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ─── On-Call stat card ────────────────────────────────────────────────────────

interface OnCallCardProps {
  isOnCall: boolean
  sessionEndTime?: string
  tz: string
  loading?: boolean
}

function OnCallCard({ isOnCall, sessionEndTime, tz, loading }: OnCallCardProps) {
  const { t } = useTranslation()
  if (loading) return <Skeleton variant="rounded" height="100%" sx={{ minHeight: 110 }} />

  const endsAt = isOnCall && sessionEndTime
    ? dayjs(sessionEndTime).tz(tz).format('MMM D, HH:mm')
    : null

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dashboard.onCallStatus')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <StatusDot active={isOnCall} />
              <Typography variant="h6" fontWeight={700}>
                {isOnCall ? t('dashboard.onDuty') : t('dashboard.offDuty')}
              </Typography>
            </Stack>
            {endsAt && (
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.ends', { time: endsAt })}
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 1.2, borderRadius: 2, background: `${isOnCall ? '#2196f3' : '#9e9e9e'}22`, color: isOnCall ? '#2196f3' : '#9e9e9e', display: 'flex' }}>
            <HeadsetMicIcon />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ─── Next-shift stat card ─────────────────────────────────────────────────────

interface NextShiftCardProps {
  shift?: ShiftDto
  tz: string
  loading?: boolean
}

function NextShiftCard({ shift, tz, loading }: NextShiftCardProps) {
  const { t } = useTranslation()
  if (loading) return <Skeleton variant="rounded" height="100%" sx={{ minHeight: 110 }} />

  const start = shift ? dayjs(shift.startTime).tz(tz) : null
  const end   = shift ? dayjs(shift.endTime).tz(tz)   : null
  const sameDay = start && end && start.isSame(end, 'day')

  const period = start && end
    ? `${start.format('MMM D, HH:mm')} – ${sameDay ? end.format('HH:mm') : end.format('MMM D, HH:mm')}`
    : null

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('dashboard.nextShift')}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {period ?? t('dashboard.noneScheduled')}
            </Typography>
            {start && (
              <Typography variant="caption" color="text.secondary">
                {start.fromNow()}
              </Typography>
            )}
            {!shift && (
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.noUpcomingShifts')}
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 1.2, borderRadius: 2, background: `${shift ? '#4285F4' : '#9e9e9e'}22`, color: shift ? '#4285F4' : '#9e9e9e', display: 'flex' }}>
            <AccessTimeIcon />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ─── Recent incidents table card ─────────────────────────────────────────────

interface RecentIncidentsCardProps {
  incidents: IncidentResponse[]
  tz: string
  loading?: boolean
  currentUserId?: string
}

function RecentIncidentsCard({ incidents, tz, loading, currentUserId }: RecentIncidentsCardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="h6">{t('dashboard.recentIncidents')}</Typography>
        </Stack>
        {loading ? (
          <Stack spacing={1}>
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
          </Stack>
        ) : incidents.length === 0 ? (
          <Stack direction="row" alignItems="center" spacing={1} py={2}>
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} />
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.noIncidents')}
            </Typography>
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor, py: 1.2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.table.started')}</TableCell>
                  <TableCell>{t('dashboard.table.status')}</TableCell>
                  <TableCell>{t('dashboard.table.confirmed')}</TableCell>
                  <TableCell>{t('dashboard.table.notes')}</TableCell>
                  <TableCell>{t('dashboard.table.investigation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incidents.map((inc) => (
                  <TableRow key={inc.incidentId} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {dayjs(inc.startedAt).tz(tz).format('MMM D, HH:mm')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(inc.startedAt).fromNow()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IncidentStatusChip status={inc.status} />
                    </TableCell>
                    <TableCell>
                      {inc.confirmedAt ? (
                        <Box>
                          <Typography variant="body2" noWrap>
                            {dayjs(inc.confirmedAt).tz(tz).format('MMM D, HH:mm')}
                          </Typography>
                          {inc.confirmedBy && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t('common.by', {
                                name: inc.confirmedBy === currentUserId
                                  ? t('common.me')
                                  : inc.confirmedBy,
                              })}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={inc.notes || ''} placement="top-start" disableHoverListener={!inc.notes}>
                        <Typography
                          variant="body2"
                          color={inc.notes ? 'text.primary' : 'text.secondary'}
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                        >
                          {inc.notes || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={inc.investigationMinutes ? 'text.primary' : 'text.secondary'}>
                        {inc.investigationMinutes ? t('common.min', { count: inc.investigationMinutes }) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { data, isLoading, isError } = useDashboard()
  const { data: activeIncident = null } = useActiveIncident()  // shared polling cache
  const { data: account } = useAccount()
  const { t } = useTranslation()
  const tz = account?.timezone ?? 'UTC'

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t('dashboard.loadError')}
      </Alert>
    )
  }

  const isOnCall       = data?.isOnCall ?? false
  const currentSession = data?.currentSession ?? null
  const nextShift      = data?.upcomingSession ?? undefined
  const recent         = data?.recentIncidents ?? []

  return (
    <Box>
      {/* Active incident card with confirm action */}
      {!isLoading && activeIncident && (
        <ActiveIncidentCard incident={activeIncident} tz={tz} />
      )}

      <Grid container spacing={3}>
        {/* Row 1 — status cards */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <OnCallCard
            loading={isLoading}
            isOnCall={isOnCall}
            sessionEndTime={currentSession?.endTime}
            tz={tz}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NextShiftCard
            loading={isLoading}
            shift={nextShift}
            tz={tz}
          />
        </Grid>

        {/* Row 2 — recent incidents, full width */}
        <Grid size={{ xs: 12 }}>
          <RecentIncidentsCard
            loading={isLoading}
            incidents={recent}
            tz={tz}
            currentUserId={account?.userId}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
